import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, GraduationCap, HeartHandshake, Users, ArrowRight, Quote, Star, Facebook, Instagram, Youtube, Music2, Clock } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SEO } from "@/components/SEO";
import { SectionTitle, VerifyBadge } from "@/components/shared";

const FEATURES = [
  { icon: GraduationCap, title: "Player Development", desc: "Age-appropriate coaching that helps players develop fundamental rugby skills and understanding of the game." },
  { icon: ShieldCheck, title: "Qualified Coaches", desc: "Dedicated coaches with relevant rugby qualifications, practical experience and a passion for developing young players." },
  { icon: HeartHandshake, title: "Safety First", desc: "A strong emphasis on safe technique, player welfare, discipline and positive coaching." },
  { icon: Users, title: "Community & Character", desc: "Building confidence, teamwork, respect, friendships and resilience through rugby." },
];

export default function Home() {
  const { settings } = useSettings();
  const [coaches, setCoaches] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [page, setPage] = useState({});
  const [nexus, setNexus] = useState({});
  const [untamed, setUntamed] = useState({});
  const [social, setSocial] = useState({});
  const [partners, setPartners] = useState({});

  useEffect(() => {
    (async () => {
      const [c, a, t, s, p, rn, ug, sm, pt] = await Promise.all([
        api.get("/public/coaches"), api.get("/public/age_groups"),
        api.get("/public/testimonials"), api.get("/public/sponsors"), api.get("/public/page/home"),
        api.get("/public/page/rugby_nexus"), api.get("/public/page/untamed_gear"), api.get("/public/page/social_media"),
        api.get("/public/page/partners"),
      ]);
      setCoaches(c.data.slice(0, 4));
      setAgeGroups(a.data.slice(0, 8));
      setTestimonials(t.data);
      setSponsors(s.data.filter((x) => x.active));
      setPage(p.data?.content || {});
      setNexus(rn.data?.content || {});
      setUntamed(ug.data?.content || {});
      setSocial(sm.data?.content || {});
      setPartners(pt.data?.content || {});
    })();
  }, []);

  const regOpen = settings?.registration_status === "open" || settings?.registration_status === "enquiries_open";

  return (
    <>
      <SEO title={settings?.seo_title || "Brackenfell Junior Rugby Club"} description={settings?.seo_description} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#12375C]">
        {page.hero_image && (
          <img
            src={mediaUrl(page.hero_image)}
            alt="Youth rugby action at Brackenfell Junior Rugby Club"
            fetchPriority="high"
            decoding="async"
            className="hero-fade-in absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start px-4 py-28 sm:px-6 sm:py-36 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            {regOpen && (
              <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#E8A317] px-4 py-1.5 text-xs font-extrabold uppercase tracking-wider text-[#0A1626]">
                <Star className="h-3.5 w-3.5" /> Enquiries open — join the family
              </span>
            )}
            {page.hero_headline ? (
              <h1 className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
                {page.hero_headline}
              </h1>
            ) : (
              <h1 className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
                The Home for{" "}
                <span className="text-[#2E7DF6]">Brackenfell's Young Rugby Players</span>
              </h1>
            )}
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200">
              {page.hero_subtext || "Brackenfell Junior Rugby Club provides a safe, structured and enjoyable environment where young players can develop their rugby skills, build confidence, form friendships and grow through the values of rugby."}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/join" data-testid="home-hero-join-cta" className="inline-flex items-center gap-2 rounded-full bg-[#E8A317] px-7 py-3.5 font-extrabold uppercase tracking-wide text-[#0A1626] shadow-lg transition-all hover:bg-[#CC8F12] active:scale-95">
                Join BJRC <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/about" data-testid="home-hero-explore-cta" className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 px-7 py-3.5 font-bold uppercase tracking-wide text-white transition-all hover:bg-white hover:text-[#0B2A4A]">
                Discover Our Club
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WELCOME TO BJRC */}
      {page.welcome_image && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="overflow-hidden rounded-3xl shadow-lg">
              <img src={mediaUrl(page.welcome_image)} alt="Brackenfell Junior Rugby Club players and coaches" loading="lazy" className="aspect-[4/3] h-full w-full object-cover" />
            </div>
            <div>
              <SectionTitle eyebrow="Welcome to BJRC" title="One Club, One Family" subtitle="From our youngest players through to our senior youth teams, BJRC is a close-knit rugby family built on teamwork, respect and a shared love of the game." />
              <Link to="/about" className="mt-6 inline-flex items-center gap-1 font-bold uppercase text-[#1E63D6] transition-all hover:gap-2">About our club <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <SectionTitle center eyebrow="Why Choose BJRC?" title="Built for Young Players to Thrive" subtitle="Everything we do is designed to help your child grow — on and off the field." />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`feature-card-${i}`}>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#0B2A4A] text-[#E8A317] transition-colors group-hover:bg-[#1E63D6] group-hover:text-white">
                <f.icon className="h-7 w-7" />
              </div>
              <h3 className="mt-5 font-heading text-xl font-bold uppercase tracking-tight text-[#0B2A4A]">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PLAYER DEVELOPMENT */}
      {page.development_image && (
        <section className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl">
            <img src={mediaUrl(page.development_image)} alt="BJRC coaching and player development" loading="lazy" className="h-72 w-full object-cover sm:h-96" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071B30]/90 via-[#071B30]/30 to-transparent" />
            <div className="absolute bottom-0 p-8 sm:p-10">
              <h3 className="font-heading text-3xl font-black uppercase text-white sm:text-4xl">Player Development First</h3>
              <p className="mt-2 max-w-xl text-slate-200">Age-appropriate coaching that builds real rugby skills, confidence and a deeper understanding of the game.</p>
            </div>
          </div>
        </section>
      )}

      {/* AGE GROUPS */}
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionTitle eyebrow="Our Age Groups" title="Rugby for Every Stage" subtitle="Age-appropriate development pathways from the mini's through to the senior youth groups." />
            <Link to="/age-groups" className="inline-flex items-center gap-1 font-bold uppercase text-[#1E63D6] hover:gap-2 transition-all">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ageGroups.map((g) => (
              <div key={g.id} className="group overflow-hidden rounded-2xl border border-slate-200 bg-[#F6F8FC] transition-all hover:shadow-lg" data-testid={`home-age-card-${g.name}`}>
                <div className="relative h-40 bg-[#0B2A4A]">
                  {g.image_url ? <img src={mediaUrl(g.image_url)} alt={g.name} className="h-full w-full object-cover" /> : (
                    <div className="flex h-full items-center justify-center font-heading text-6xl font-black text-white/15">{g.name}</div>
                  )}
                  <span className="absolute left-4 top-4 rounded-lg bg-[#E8A317] px-3 py-1 font-heading text-lg font-extrabold text-[#0A1626]">{g.name}</span>
                </div>
                <div className="p-6">
                  <p className="text-sm leading-relaxed text-slate-600 line-clamp-3">{g.description}</p>
                  <Link to="/age-groups" className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase text-[#1E63D6]">Learn more <ArrowRight className="h-4 w-4" /></Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COACHES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionTitle eyebrow="Meet Our Coaches" title="Dedicated Coaching Team" />
          <Link to="/coaches" className="inline-flex items-center gap-1 font-bold uppercase text-[#1E63D6] hover:gap-2 transition-all">Full directory <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {coaches.map((c) => (
            <div key={c.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white text-center shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`home-coach-${c.id}`}>
              <div className="h-56 bg-gradient-to-b from-[#0B2A4A] to-[#1E63D6]">
                {c.photo_url ? <img src={mediaUrl(c.photo_url)} alt={c.name} className="h-full w-full object-cover object-top" /> : (
                  <div className="flex h-full items-center justify-center font-heading text-5xl font-black text-white/25">{c.name.split(" ").map((n) => n[0]).slice(0,2).join("")}</div>
                )}
              </div>
              <div className="p-5 text-left">
                <h3 className="font-heading text-lg font-bold uppercase text-[#0B2A4A]">{c.name}</h3>
                <p className="text-sm text-slate-500">{c.role}{c.age_group ? ` · ${c.age_group}` : ""}</p>
                {c.qualifications && (
                  <p className="mt-3 flex gap-2 text-xs text-slate-600" data-testid={`home-coach-qual-${c.id}`}>
                    <GraduationCap className="h-4 w-4 shrink-0 text-[#1E63D6]" />
                    <span>{c.qualifications}</span>
                  </p>
                )}
                {c.years_experience && (
                  <p className="mt-2 flex gap-2 text-xs text-slate-600" data-testid={`home-coach-exp-${c.id}`}>
                    <Clock className="h-4 w-4 shrink-0 text-[#1E63D6]" />
                    <span>{c.years_experience}</span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MORE THAN RUGBY */}
      {page.more_than_rugby_image && (
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <div>
              <SectionTitle eyebrow="More Than Rugby" title="Building Character & Friendships" subtitle="We develop young people as much as young players — teaching teamwork, respect, discipline and resilience that last a lifetime." />
            </div>
            <div className="overflow-hidden rounded-3xl shadow-lg lg:order-last">
              <img src={mediaUrl(page.more_than_rugby_image)} alt="BJRC players in a team huddle" loading="lazy" className="aspect-[4/3] h-full w-full object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-[#0B2A4A] py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionTitle light center eyebrow="Parent Testimonials" title="Loved by Our Rugby Families" />
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t) => (
                <div key={t.id} className="rounded-2xl bg-white/5 p-7 ring-1 ring-white/10">
                  <Quote className="h-8 w-8 text-[#E8A317]" />
                  <p className="mt-4 text-slate-200">"{t.quote}"</p>
                  <p className="mt-4 font-bold text-white">{t.author}</p>
                  {t.location && <p className="text-sm text-slate-400">{t.location}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RUGBY NEXUS */}
      {nexus.visible && nexus.show_on_home && (
        <section className="bg-[#050914] py-16 sm:py-20" data-testid="home-rugby-nexus">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <div>
              <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#2E7DF6]">Player Development</span>
              <h2 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{nexus.home_heading}</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-300">{nexus.home_text}</p>
              <Link to="/rugby-nexus" data-testid="home-rugby-nexus-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#E8A317] px-7 py-3.5 font-extrabold uppercase tracking-wide text-[#0A1626] transition-all hover:bg-[#CC8F12] active:scale-95">
                {nexus.home_button || "Learn About Rugby Nexus"} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            {nexus.home_image && (
              <div className="flex justify-center lg:order-last">
                <div className="relative">
                  <div className="absolute -inset-6 rounded-full bg-[#1E63D6]/25 blur-3xl" />
                  <div className="relative mx-auto w-full max-w-[240px] rounded-[2rem] border border-white/15 bg-[#0A1128] p-2 shadow-2xl">
                    <div className="aspect-[640/1385] overflow-hidden rounded-[1.6rem] bg-[#050914]">
                      <img src={mediaUrl(nexus.home_image)} alt="Rugby Nexus" loading="lazy" className="h-full w-full object-cover object-top" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* UNTAMED SPORTS GEAR */}
      {untamed.visible && untamed.show_on_home && (
        <section className="bg-white py-16 sm:py-20" data-testid="home-untamed-gear">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            {untamed.home_image && (
              <div className="overflow-hidden rounded-3xl shadow-lg">
                <img src={mediaUrl(untamed.home_image)} alt="Untamed Sports Gear" loading="lazy" className="aspect-[4/3] h-full w-full object-cover" />
              </div>
            )}
            <div>
              <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#1E63D6]">Official Kit & Equipment Supplier</span>
              <h2 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-[#0B2A4A] sm:text-4xl">{untamed.home_heading}</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{untamed.home_text}</p>
              <Link to="/untamed-gear" data-testid="home-untamed-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0B2A4A] px-7 py-3.5 font-extrabold uppercase tracking-wide text-white transition-all hover:bg-[#071B30] active:scale-95">
                {untamed.home_button || "Visit Untamed Gear"} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* OUR PARTNERS STRIP */}
      {partners.visible && (partners.items || []).length > 0 && (
        <section className="border-y border-slate-200 bg-[#F6F8FC] py-10" data-testid="home-partners-strip">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="text-center font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#1E63D6]">{partners.heading || "Our Partners"}</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {(partners.items || []).map((it, i) => (
                <Link key={i} to={it.link || "#"} data-testid={`home-partner-${i}`}
                  className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                  {it.logo_url && <img src={mediaUrl(it.logo_url)} alt={it.name} loading="lazy" className="h-14 w-14 shrink-0 rounded-xl border border-slate-100 object-contain p-1" />}
                  <div className="flex-1">
                    <div className="font-heading text-base font-bold uppercase tracking-tight text-[#0B2A4A]">{it.name}</div>
                    <div className="text-sm text-slate-500">{it.tagline}</div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPONSORS */}
      {sponsors.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <SectionTitle center eyebrow="Our Sponsors" title="Proudly Supported By" />
          <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
            {sponsors.map((s) => (
              <a key={s.id} href={s.website || "#"} target={s.website ? "_blank" : undefined} rel="noreferrer" className="flex h-28 items-center justify-center rounded-xl border border-slate-200 bg-white p-4 transition-all hover:shadow-md">
                {s.logo_url ? <img src={mediaUrl(s.logo_url)} alt={s.name} className="max-h-16 max-w-full object-contain" /> : <span className="text-center text-sm font-bold text-slate-500">{s.name}</span>}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* FOLLOW THE BJRC ACTION */}
      {social.show_on_home && (social.platforms || []).some((p) => p.enabled && p.url) && (
        <section className="bg-[#0B2A4A] py-16 sm:py-20" data-testid="home-social">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#E8A317]">Stay Connected</span>
              <h2 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{social.heading || "Follow The BJRC Action"}</h2>
              {social.description && <p className="mt-3 text-base leading-relaxed text-slate-300">{social.description}</p>}
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {(social.platforms || []).filter((p) => p.enabled && p.url).map((p) => {
                  const Icon = { facebook: Facebook, instagram: Instagram, youtube: Youtube, tiktok: Music2 }[p.key] || ArrowRight;
                  return (
                    <a key={p.key} href={p.url} target="_blank" rel="noreferrer noopener" data-testid={`home-social-${p.key}`}
                      className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 font-bold uppercase tracking-wide text-white ring-1 ring-white/20 transition-all hover:bg-[#E8A317] hover:text-[#0A1626]">
                      <Icon className="h-5 w-5" /> Follow on {p.label}
                    </a>
                  );
                })}
              </div>
            </div>
            {(social.featured_posts || []).length > 0 && (
              <div className="mt-12 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
                {(social.featured_posts || []).slice(0, 6).map((post, i) => (
                  <a key={i} href={post.url || "#"} target="_blank" rel="noreferrer noopener" data-testid={`home-social-post-${i}`}
                    className="group relative aspect-square overflow-hidden rounded-xl bg-white/5 ring-1 ring-white/10">
                    {post.image && <img src={mediaUrl(post.image)} alt={post.caption || "BJRC highlight"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  </a>
                ))}
              </div>
            )}
            <div className="mt-8 text-center">
              <Link to="/social-media" data-testid="home-social-viewall" className="inline-flex items-center gap-1 font-bold uppercase text-[#E8A317] transition-all hover:gap-2">View all social <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* JOIN CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#E8A317] px-8 py-14 text-center sm:px-16">
          <h2 className="font-heading text-3xl font-black uppercase tracking-tight text-[#0A1626] sm:text-5xl">Join the BJRC Family</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg font-medium text-[#0A1626]/80">
            Give your child a safe, structured and enjoyable start in rugby. Enquire today and our team will be in touch.
          </p>
          <Link to="/join" data-testid="home-cta-join" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0B2A4A] px-8 py-4 font-extrabold uppercase tracking-wide text-white transition-all hover:bg-[#071B30] active:scale-95">
            Enquire to Join <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </>
  );
}
